import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from '../config/api';

export default function ForgotPassword() {
  const location = useLocation();
  const startOnCode = location.pathname === '/reset-password';
  const [step, setStep] = useState<'email' | 'code'>(startOnCode ? 'code' : 'email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const requestCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setBusy(true);
    try {
      const response = await axios.post('/api/auth/forgot-password', { email });
      setMessage(response.data?.message || 'If an account exists for that email, we sent a code.');
      setStep('code');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not send a code. Try again in a few minutes.');
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setBusy(true);
    try {
      const response = await axios.post('/api/auth/reset-password', { email, code, password });
      setMessage(response.data?.message || 'Password updated. You can sign in.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not reset the password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-amber-100 to-amber-50 px-4">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md border border-amber-100">
        <h1 className="text-2xl font-bold text-amber-800 mb-2">Reset your password</h1>
        <p className="text-sm text-gray-600 mb-4">
          We email a 6-digit code. It expires in 15 minutes.
        </p>
        {message && <p className="mb-3 rounded bg-green-50 text-green-800 p-3 text-sm">{message}</p>}
        {error && <p className="mb-3 rounded bg-red-50 text-red-700 p-3 text-sm">{error}</p>}

        {step === 'email' ? (
          <form onSubmit={requestCode}>
            <label className="block text-gray-700 mb-2" htmlFor="reset-email">
              Email
            </label>
            <input
              id="reset-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full px-4 py-2 border rounded-lg mb-4"
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-amber-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
          </form>
        ) : (
          <form onSubmit={resetPassword} className="space-y-3">
            <div>
              <label className="block text-gray-700 mb-1" htmlFor="reset-email-code">
                Email
              </label>
              <input
                id="reset-email-code"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-1" htmlFor="reset-code">
                6-digit code
              </label>
              <input
                id="reset-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-2 border rounded-lg tracking-widest"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-1" htmlFor="reset-password">
                New password
              </label>
              <input
                id="reset-password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">At least 8 characters.</p>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-amber-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save new password'}
            </button>
            <button
              type="button"
              className="w-full text-sm text-amber-800"
              onClick={() => setStep('email')}
            >
              Send another code
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-gray-600 text-sm">
          <Link to="/login" className="text-amber-800 font-semibold">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
